/**
 * PresentDashboardPresenter
 * --
 */
import React from 'react';
import { Content, Row, Col, Title, Text } from 'components';
import { Empty, Flex, Spin, Statistic, Tabs } from 'antd';
import dayjs from 'dayjs';
import BarChartComp from './components/BarChartComp';
import SalesTable from './components/SalesTable';
import SummaryTable from './components/SummaryTable';
import ProductChart from './components/ProductChart';

const PresentDashboardPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    data = [],
    isLoading = false,
    startDate = null,
    endDate = null,
    format = 'YYYY.MM.DD',
  } = props;

  /* ===== STATE ===== */
  // 탭 키
  const [tabKey, setTabKey] = React.useState('ALL');

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
  };

  /**
   * 탭 아이템
   * --
   */
  const tabItem = [
    {
      key: 'ALL',
      label: '전체 선물 현황',
    },
    {
      key: 'PRODUCT',
      label: '상품 선물 현황',
    },
    {
      key: 'MENU',
      label: '메뉴 선물 현황',
    },
  ];

  /* ===== FUNCTION ===== */
  /**
   * 탭뷰 렌더
   * --
   * @param {string} key
   * @returns
   */
  const renderTabView = (key) => {
    switch (key) {
      case 'ALL':
        return (
          <Flex gap={24} justify="space-between">
            <div style={{ flex: 1, minHeight: 500 }}>
              <BarChartComp data={data} slice={7} />
            </div>
            <div style={{ flex: 1.5, maxWidth: 1300 }}>
              <SalesTable data={data} />
            </div>
          </Flex>
        );
      case 'PRODUCT':
      case 'MENU':
        return (
          <Flex gap={24} justify="space-between">
            <div style={{ flex: 1, minHeight: 500 }}>
              <ProductChart data={data} type={key} slice={7} />
            </div>
            <div style={{ flex: 1.5, maxWidth: 1300 }}>
              <SummaryTable data={data} type={key} />
            </div>
          </Flex>
        );
      default:
        return (
          <Content maxWidth={'100%'}>
            <Empty />
          </Content>
        );
    }
  };

  /**
   * 요약 기본값 호출
   * --
   * @returns {object}
   */
  const getInitialSummary = () => ({
    MONTHLY_TOTAL_COUNT: {
      title: '이번달 총 주문건수',
      value: 0,
      suffix: '건',
    },
    MONTHLY_TOTAL_PAYMENT_PRICE: {
      title: (
        <>
          이번달 실 결제금액
          <br />
          (금액권 포함)
        </>
      ),
      value: 0,
      suffix: '원',
    },
    MONTHLY_TOTAL_REFUND_PRICE: {
      title: (
        <>
          이번달 환불금액
          <br />
          (금액권 포함)
        </>
      ),
      value: 0,
      suffix: '원',
    },
    AMOUNT_TOTAL_PAYMENT_PRICE: {
      title: '이번달 금액권 실 결제금액',
      value: 0,
      suffix: '원',
    },
    AMOUNT_TOTAL_REFUND_PRICE: {
      title: '이번달 금액권 환불금액',
      value: 0,
      suffix: '원',
    },
  });

  /* ===== HOOKS ===== */
  /**
   * 이번달 선물 요약 정보
   * --
   */
  const MonthlySummary = React.useMemo(() => {
    if (!data.length) return getInitialSummary();

    const summary = data.reduce((acc, cur) => {
      ++acc['MONTHLY_TOTAL_COUNT']['value'];

      const { payment_state, payment_price } = cur;
      const { type } = cur.product;
      if (payment_state === 'COMPLETED') {
        acc['MONTHLY_TOTAL_PAYMENT_PRICE']['value'] += payment_price;
      } else {
        acc['MONTHLY_TOTAL_REFUND_PRICE']['value'] += payment_price;
      }

      if (payment_state === 'COMPLETED' && type === 'AMOUNT') {
        acc['AMOUNT_TOTAL_PAYMENT_PRICE']['value'] += payment_price;
      } else if (payment_state === 'CANCELLED' && type === 'AMOUNT') {
        acc['AMOUNT_TOTAL_REFUND_PRICE']['value'] += payment_price;
      }

      return acc;
    }, getInitialSummary());

    return summary;
  }, [data]);

  /* ===== RENDER ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row gutter={[16, 16]}>
            {/* SECTION: 타이틀 */}
            <Col x={24}>
              <Flex justify="space-between" align="center">
                <Title style={{ ...styles.title }}>선물 내역</Title>

                <Text size={16} style={{ opacity: 0.7 }}>
                  조회기간 {dayjs.unix(startDate).format(format)} ~{' '}
                  {dayjs.unix(endDate).format(format)}
                </Text>
              </Flex>
            </Col>
            {/* !SECTION: 타이틀 */}

            {/* SECTION: 요약 */}
            <Col x={24}>
              <Flex gap={8}>
                {Object.values(MonthlySummary).map((_item, _index) => (
                  <div
                    key={`${_item.label}-${_index}`}
                    className="boxShadow"
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 4,
                      flex: 1,
                      padding: '15px 24px',
                    }}
                  >
                    <Statistic
                      className="cursorOverOpacity"
                      direction={'row'}
                      size="large"
                      title={_item.title}
                      value={_item.value}
                      suffix={_item.suffix}
                      valueStyle={{
                        color: '#1677ff',
                        // textAlign: 'center',
                        fontSize: '2.15em',
                      }}
                    />
                  </div>
                ))}
              </Flex>
            </Col>
            {/* !SECTION: 요약 */}

            {/* SECTION: 내용 */}
            <Col x={24}>
              <Tabs
                type="card"
                size="large"
                onChange={(key) => setTabKey(key)}
                items={tabItem}
                tabBarStyle={{ marginBottom: 0 }}
              />
              <Content maxWidth={'100%'} style={{ minHeight: 500 }}>
                {renderTabView(tabKey)}
              </Content>
            </Col>
            {/* !SECTION: 내용 */}
          </Row>
        </Content>
      </Spin>
    </React.Fragment>
  );
};

export default PresentDashboardPresenter;
